import { padStart } from 'lodash-es';
import { emitLog } from './log';
import { getBytes } from './utils';

const formatTo16 = (num: number, bytes = 1) => {
  return padStart(Number(num).toString(16), bytes * 2, '0');
};

const formatTo10 = (num: number, bytes = 1) => {
  return padStart(Number(num).toString(), bytes * 2, '0');
};

const formatValueByNumType = (num: number, bytes: number, numType: 'hex' | 'dec') => {
  if (numType === 'dec') {
    return formatTo10(num, bytes);
  }
  return formatTo16(num, bytes);
};

const formatValueByStorage = (value: string, storageType: 'be' | 'le') => {
  if (storageType === 'le') {
    const result: string[] = [];
    for (let i = 0; i < value.length; i += 2) {
      result.splice(0, 0, value.slice(i, i + 2));
    }
    return result.join('');
  }
  return value;
};

/**
 * 格式化数值为字符串
 * @param num 值
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatNumber = (num: number, config: DpMapItem, result: any) => {
  const { extension, numType = 'hex', storageType = 'be' } = config;
  const bytes = getBytes(config, result);
  let value = num;
  if (extension) {
    const { min = -Infinity, max = Infinity } = extension;
    value = Math.max(Math.min(value, max), min);
  }
  return formatValueByStorage(formatValueByNumType(value, bytes, numType), storageType);
};

/**
 * 格式化有符号数值为字符串
 * @param num 值
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatSignedNumber = (num: number, config: DpMapItem, result: any) => {
  const { extension, numType = 'hex', storageType = 'be' } = config;
  const bytes = getBytes(config, result);
  let value = num;
  if (extension) {
    const { min = -Infinity, max = Infinity } = extension;
    value = Math.max(Math.min(value, max), min);
  }
  // 是否为负值
  if (value < 0) {
    let base = 0; // 数值的范围
    switch (bytes) {
      case 1:
        base = 0xff;
        break;
      case 2:
        base = 0xffff;
        break;
      case 3:
        base = 0xffffff;
        break;
      case 4:
        base = 0xffffffff;
        break;
      default:
        console.warn('有符号数值最大支持32位数值，无法表示');
    }
    value = -value;
    // 取反码+1
    value = ((value & (base >> 1)) ^ base) + 1;
  }

  return formatValueByStorage(formatValueByNumType(value, bytes, 'hex'), storageType);
};

/**
 * 将布尔值格式化为字符串
 * @param value 布尔值
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatBoolean = (value: boolean, config: DpMapItem, result: any) => {
  return formatNumber(Number(value), config, result);
};

/**
 * 将枚举值格式化为字符串
 * @param num 枚举值
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatEnum = (num: number, config: DpMapItem, result: any) => {
  const { extension, name } = config;
  let value = num;
  if (extension) {
    // 是否有扩展值，则检验是否值标准
    const { range } = extension;
    if (Array.isArray(range) && range.length) {
      if (range.includes(num)) {
        value = num;
      } else {
        // 提示校验失败
        emitLog({
          type: 'warning',
          message: `属性【${name}】的值${num}不在可枚举范围内`,
          data: {
            [name]: num,
          },
        });
        //返回了枚举第一个值
        value = range[0];
      }
    }
  }
  return formatNumber(value, config, result);
};

/**
 * 将位序列格式化为字符串
 * @param bits 位序列
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatBit = (bits: number[], config: DpMapItem, res: any) => {
  const { storageType = 'be', numType = 'hex', bitType = 'right' } = config;
  const bytes = getBytes(config, res);
  const result: string[] = [];
  // 每8位一个字节
  for (let i = 0; i < bytes; i++) {
    const start = i * 8;
    const end = (i + 1) * 8;
    const byteBits = bits.slice(start, end);
    // 数据是否准确
    if (byteBits.length < 8) {
      const moreCount = 8 - byteBits.length;
      for (let j = 0; j < moreCount; j++) {
        byteBits.push(0);
      }
    }

    if (bitType === 'right') {
      byteBits.reverse();
    }

    const value = parseInt(byteBits.join(''), 2);
    result.push(formatValueByNumType(value, 1, numType));
  }
  return formatValueByStorage(result.join(''), storageType);
};

/**
 * 将位序列格式化为字符串
 * @param bits 位序列
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatBits = (bits: number[], config: DpMapItem, res: any) => {
  const { storageType = 'be', numType = 'hex', bitType = 'right' } = config;
  const bytes = getBytes(config, res);
  const result: string[] = [];
  bits = [...bits];
  const bitLength = bytes * 8;
  // 是否为8的倍数，不是，则补0
  if (bits.length % 8 !== bitLength) {
    for (let i = bits.length; i < bitLength; i++) {
      bits.push(0);
    }
  }
  // 如果是从低位开始的，这里先返排序
  if (bitType === 'right') {
    bits.reverse();
  }
  // 每8位一个字节
  for (let i = 0; i < bytes; i++) {
    const start = i * 8;
    const end = (i + 1) * 8;
    const byteBits = bits.slice(start, end);
    const value = parseInt(byteBits.join(''), 2);
    result.push(formatValueByNumType(value, 1, numType));
  }
  return formatValueByStorage(result.join(''), storageType);
};

/**
 * 将星期格式化为字符串
 * @param bits 星期数据
 * @param config 属性配置
 * @param result 当前同级数据
 * @returns
 */
export const formatWeek = (bits: number[], config: DpMapItem, res: any) => {
  return formatBit([...bits, 0], config, res);
};

export const formatString = (value: string, config: DpMapItem, res: any) => {
  const { storageType = 'be', name } = config;
  const bytes = getBytes(config, res);
  const targetLength = bytes * 2;
  let temp = value;
  // 检查长度是否正确，确保数据准确性
  if (value.length > targetLength) {
    // 提示值长度过长
    emitLog({
      type: 'warning',
      message: `属性【${name}】转化后的值${value}长度过长，超过${bytes}个字节`,
      data: { [name]: value },
    });
    temp = value.slice(0, targetLength);
  } else if (value.length < targetLength) {
    // 提示值长度过短
    emitLog({
      type: 'warning',
      message: `属性【${name}】转化后的值${value}长度过短，少于${bytes}个字节`,
      data: { [name]: value },
    });
    temp = padStart(value, targetLength, '0');
  }
  return formatValueByStorage(temp, storageType);
};

export const formatCustom = (value: any, config: DpMapItem, res: any) => {
  const { format, name } = config;
  let temp = value?.toString();
  if (typeof format === 'function') {
    temp = format(value);
  } else {
    // 提示反解析方法未设置或不正确
    emitLog({
      type: 'warning',
      message: `属性【${name}】为自定义类型，检测到未配置 format 处理函数`,
      data: { [name]: value },
    });
  }
  return formatString(temp, config, res);
};

export const formatNumberBySeparate = (num: number, config: DpMapItem, res: any) => {
  const { extension, numType = 'hex', storageType = 'be' } = config;
  const bytes = getBytes(config, res);
  let value = num % 10 ** (bytes * 2);
  if (extension) {
    const { min = -Infinity, max = Infinity } = extension;
    value = Math.max(Math.min(value, max), min);
  }
  const result: string[] = [];
  // 根据字节数，分隔数据
  for (let i = 0; i < bytes; i++) {
    const baise10 = 10 ** ((bytes - 1 - i) * 2);
    const temp = Math.floor(value / baise10);
    const text = numType === 'dec' ? formatTo10(temp, 1) : formatTo16(temp, 1);
    value = value % baise10;
    if (storageType === 'le') {
      result.splice(0, 0, text);
    } else {
      result.push(text);
    }
  }

  return result.join('');
};
